import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/helpers/billing'
import { getQuotaDisplay } from '@/helpers/billingQuota'
import { BillingSubscription, PricingModel } from '@/types/billing'
import { AppDispatch, RootState } from '@/redux'
import { useDispatch, useSelector } from 'react-redux'
import { createPortalSession } from '@/services/billingService'
import { showNotification } from '@/services/notificationService'
import { Check, X, AlertTriangle, ShieldCheck, ShieldX, ShieldOff, Hourglass, BookOpen, ExternalLink } from 'lucide-react'
import { DEFAULT_LEGACY_PLAN, LEGACY_TIER_DETAILS } from './legacyPlanDetails'

/** Playbook-based pricing (api.json / product guide): first $150/mo, each additional $75/mo. */
const FIRST_PLAYBOOK_MONTHLY_CENTS = 15000
const ADDITIONAL_PLAYBOOK_MONTHLY_CENTS = 7500

function getPlaybookMonthlyBreakdown(playbookCount: number): { label: string; amountCents: number }[] {
  if (playbookCount < 1) return []
  const lines: { label: string; amountCents: number }[] = [
    { label: 'First playbook', amountCents: FIRST_PLAYBOOK_MONTHLY_CENTS }
  ]
  if (playbookCount > 1) {
    const additional = playbookCount - 1
    lines.push({
      label: `Additional ${additional} playbook${additional !== 1 ? 's' : ''} @ ${formatCurrency(ADDITIONAL_PLAYBOOK_MONTHLY_CENTS / 100)}/mo`,
      amountCents: additional * ADDITIONAL_PLAYBOOK_MONTHLY_CENTS
    })
  }
  return lines
}

function getPlaybookMonthlyTotalCents(playbookCount: number): number {
  if (playbookCount < 1) return 0
  return FIRST_PLAYBOOK_MONTHLY_CENTS + (playbookCount - 1) * ADDITIONAL_PLAYBOOK_MONTHLY_CENTS
}

interface CurrentPlanProps {
  subscription?: BillingSubscription | null
  /** Required for pending-increase CTA (Open Customer Portal) */
  token?: string
  organizationId?: string
}

function getStatusDisplay(status: string, cancelAtPeriodEnd: boolean) {
  if (cancelAtPeriodEnd) {
    return {
      label: 'Canceling',
      className: 'bg-orange-100 text-orange-800 border-orange-200 hover:text-background',
      icon: <Hourglass className="w-4 h-4 mr-2" />
    }
  }

  const statusMap: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    active: {
      label: 'Active',
      className: 'bg-green-100 text-green-800 border-green-200 hover:text-background',
      icon: <Check className="w-4 h-4 mr-2" />
    },
    canceled: {
      label: 'Canceled',
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: <X className="w-4 h-4 mr-2" />
    },
    incomplete: {
      label: 'Payment Required',
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:text-background',
      icon: <AlertTriangle className="w-4 h-4 mr-2" />
    },
    incomplete_expired: {
      label: 'Payment Expired',
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: <AlertTriangle className="w-4 h-4 mr-2" />
    },
    past_due: {
      label: 'Past Due',
      className: 'bg-orange-100 text-orange-800 border-orange-200 hover:text-background',
      icon: <ShieldOff className="w-4 h-4 mr-2" />
    },
    trialing: {
      label: 'Trial Period',
      className: 'bg-blue-100 text-blue-800 border-blue-200 hover:text-background',
      icon: <ShieldCheck className="w-4 h-4 mr-2" />
    },
    unpaid: {
      label: 'Payment Failed',
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: <ShieldX className="w-4 h-4 mr-2" />
    }
  }

  return (
    statusMap[status] ?? {
      label: status.charAt(0).toUpperCase() + status.slice(1),
      className: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: '•'
    }
  )
}

export const CurrentPlan: React.FC<CurrentPlanProps> = ({ subscription, token, organizationId }) => {
  const dispatch: AppDispatch = useDispatch()
  const { loading: appLoading } = useSelector((state: RootState) => state.app)

  if (!subscription) {
    return null
  }

  const pricingModel = subscription.pricingModel as PricingModel | undefined
  const willCancel = subscription.cancelAtPeriodEnd
  const statusDisplay = getStatusDisplay(subscription.status, willCancel)

  const handleOpenPortal = async () => {
    if (!token || !organizationId) return
    const resp = await dispatch(createPortalSession(token, organizationId))
    if (resp.data?.url) {
      window.location.href = resp.data.url
    } else {
      showNotification({ data: undefined, err: resp.err }, '', {})
    }
  }

  // Playbook-based: show playbook summary only (no tier)
  if (pricingModel === 'playbook_based_v2') {
    const quota = getQuotaDisplay(subscription)
    const { usedSlots, totalQuota, usableQuota, scheduledQuota } = quota
    const billedCount = subscription.confirmedQuota ?? subscription.usableQuota ?? 0
    const effective = usableQuota
    const target = scheduledQuota
    const hasPendingDecrease = subscription.hasPendingDecrease === true && effective > target
    const hasPendingIncrease = subscription.hasPendingIncrease === true
    const pendingQuota = subscription.pendingQuota ?? 0
    const effectiveForPending = usableQuota
    const additionalPlaybooks = Math.max(0, pendingQuota - effectiveForPending)
    const pendingChargeCents =
      effectiveForPending >= 1
        ? additionalPlaybooks * ADDITIONAL_PLAYBOOK_MONTHLY_CENTS
        : getPlaybookMonthlyTotalCents(pendingQuota)
    const pendingChargeLabel =
      effectiveForPending >= 1
        ? additionalPlaybooks === 1
          ? '1 additional playbook'
          : `${additionalPlaybooks} additional playbooks`
        : `${pendingQuota} playbook${pendingQuota !== 1 ? 's' : ''}`
    const periodEndFormatted = new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()
    const newMonthlyRateCents = target >= 1 ? getPlaybookMonthlyTotalCents(target) : 0

    return (
      <div className="px-6 py-5">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
            <Badge className={statusDisplay.className}>
              <span className="mr-1">{statusDisplay.icon}</span>
              {statusDisplay.label}
            </Badge>
          </div>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <BookOpen className="w-4 h-4 text-primary" />
          </div>
        </div>

        <div className="space-y-4">
          {hasPendingIncrease && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm font-medium text-yellow-800">
                Payment pending: <strong>{formatCurrency(pendingChargeCents / 100)}/mo</strong> for {pendingChargeLabel}.
              </p>
              {token && organizationId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={handleOpenPortal}
                  disabled={appLoading}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open Customer Portal to pay
                </Button>
              )}
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900">Playbook-based pricing</h3>
            <p className="mt-1 text-sm text-gray-600">
              {billedCount} playbook{billedCount !== 1 ? 's' : ''} in your subscription
            </p>
          </div>

          {/* What you're paying per month — use target (billed) count */}
          {billedCount >= 1 && (() => {
            const breakdown = getPlaybookMonthlyBreakdown(billedCount)
            const totalCents = getPlaybookMonthlyTotalCents(billedCount)
            return (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="mb-2 text-sm font-semibold text-gray-700">What you pay per month</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  {breakdown.map((line) => (
                    <li key={line.label} className="flex justify-between">
                      <span>{line.label}</span>
                      <span className="font-medium text-gray-900">{formatCurrency(line.amountCents / 100)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 text-sm font-semibold text-gray-900">
                  <span>Total per month</span>
                  <span>{formatCurrency(totalCents / 100)}</span>
                </div>
              </div>
            )
          })()}

          {totalQuota > 0 && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-sm font-medium text-gray-700">
                <span className="text-gray-900">{usedSlots}</span> of <span className="text-gray-900">{totalQuota}</span> playbooks used
                {typeof subscription.freeQuota === 'number' &&
                  subscription.freeQuota > 0 && (
                    <span className="ml-1 text-gray-500">
                      ({quota.subscribed} subscribed + {subscription.freeQuota} free)
                    </span>
                  )}
              </p>
            </div>
          )}

          {hasPendingDecrease && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                You have {effective} playbook{effective !== 1 ? 's' : ''} until {periodEndFormatted}. After that, your quota will be {target}.
              </p>
              <p className="mt-2 text-sm text-blue-800">
                Your new monthly charge (from {periodEndFormatted}) will be <strong>{formatCurrency(newMonthlyRateCents / 100)}</strong> for {target} playbook{target !== 1 ? 's' : ''}.
              </p>
            </div>
          )}

          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-600">Current period</span>
                <div className="font-medium text-gray-900">
                  {new Date(subscription.currentPeriodStart * 1000).toLocaleDateString()} –{' '}
                  {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-600">Next billing</span>
                <div className="font-medium text-gray-900">
                  {willCancel ? 'Plan will cancel' : new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {willCancel && (
            <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5 text-orange-600" />
                <div>
                  <h4 className="font-medium text-orange-800">Subscription ending</h4>
                  <p className="mt-1 text-sm text-orange-600">
                    Your subscription will end on{' '}
                    {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}. You can reactivate before then.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Free quota only: no paid subscription, quota granted by TCS Admin
  const isFreeQuotaOnly =
    subscription.planTier === 'free_quota_only' || subscription.status === 'free_quota_only'
  if (isFreeQuotaOnly) {
    const quota = getQuotaDisplay(subscription)
    const usedSlots = quota.usedSlots
    const total = quota.totalQuota
    return (
      <div className="px-6 py-5">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:text-background">
              <ShieldCheck className="w-4 h-4 mr-1" />
              Free quota
            </Badge>
          </div>
        </div>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            You&apos;re not on a paid subscription. Your playbook quota comes from free quota granted by your admin.
          </p>
          {total > 0 && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-sm font-medium text-gray-700">
                <span className="text-gray-900">{usedSlots}</span> of{' '}
                <span className="text-gray-900">{total}</span> playbooks used
              </p>
              {typeof subscription.freeQuota === 'number' && subscription.freeQuota > 0 && (
                <p className="mt-1 text-sm text-gray-600">Free quota: {subscription.freeQuota}</p>
              )}
            </div>
          )}
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3">
            <p className="text-sm font-semibold text-green-800">What you pay per month</p>
            <p className="mt-1 text-lg font-semibold text-green-900">$0.00</p>
            <p className="text-sm text-green-700">No active subscription</p>
          </div>
        </div>
      </div>
    )
  }

  // Legacy (plan_tier_based_v1): show tier with "Legacy" label. planTier is STARTER/PRO/POWER for v1.
  const tierKey = (subscription.planTier ?? 'starter').toString().toLowerCase()
  const plan = LEGACY_TIER_DETAILS[tierKey] ?? DEFAULT_LEGACY_PLAN

  return (
    <div className="px-6 py-5">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
          <Badge className={statusDisplay.className}>
            <span className="mr-1">{statusDisplay.icon}</span>
            {statusDisplay.label}
          </Badge>
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
            Legacy
          </Badge>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">{plan.name} Plan</h3>
            <p className="text-sm text-gray-600">{plan.credentials} credentials included</p>
          </div>
        </div>

        {/* What you pay per month — legacy tier is a single fixed price */}
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="mb-2 text-sm font-semibold text-gray-700">What you pay per month</p>
          <ul className="space-y-1 text-sm text-gray-600">
            <li className="flex justify-between">
              <span>{plan.name} plan</span>
              <span className="font-medium text-gray-900">{formatCurrency(plan.price)}</span>
            </li>
          </ul>
          <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 text-sm font-semibold text-gray-900">
            <span>Total per month</span>
            <span>{formatCurrency(plan.price)}</span>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-semibold text-gray-600">Current period</span>
              <div className="font-medium text-gray-900">
                {new Date(subscription.currentPeriodStart * 1000).toLocaleDateString()} –{' '}
                {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
              </div>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Next billing</span>
              <div className="font-medium text-gray-900">
                {willCancel ? 'Plan will cancel' : new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {willCancel && (
          <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
            <div className="flex items-center">
              <AlertTriangle className="mr-2 h-5 w-5 text-orange-600" />
              <div>
                <h4 className="font-medium text-orange-800">Subscription ending</h4>
                <p className="mt-1 text-sm text-orange-600">
                  Your subscription will end on{' '}
                  {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}. You can reactivate before then.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
